<template>
	<div
		class="flow-switch-container"
		:on="innerOn"
		:disabled="disabled"
		:tabindex="tabindex"
		:type="type"
		:size="size"
		@click="handleClick"
	>
		<div class="flow-switch-ball" />
	</div>
</template>
<script lang="ts">
import { defineComponent, PropType } from "vue";

const TYPE = ["primary", "secondary", "success", "error", "warning"] as const;
const SIZE = ["small", "medium", "large"] as const;

export type SwitchBoxType = typeof TYPE[number];
export type SwitchBoxSize = typeof SIZE[number];

/**
 * Switch allows a user to toggle the state of an item from ON to OFF or vice a versa.
 */
export default defineComponent({
	name: "SwitchBox",
	props: {
		/**
		 * true | false
		 */
		on: {
			type: Boolean,
			default: false
		},
		/**
		 * true | false
		 */
		disabled: {
			type: Boolean,
			default: false
		},
		/**
		 * tabindex number
		 *  */
		tabindex: {
			type: Number,
			default: -1
		},
		/**
		 * 'primary' | 'secondary' | 'success' | 'error' | 'warning'
		 */
		type: {
			type: String as PropType<SwitchBoxType>,
			default: "success",
			validator(value: SwitchBoxType) {
				return TYPE.includes(value);
			}
		},
		/**
		 * 'small' | 'medium' | 'large'
		 */
		size: {
			type: String as PropType<SwitchBoxSize>,
			default: "small",
			validator(value: SwitchBoxSize) {
				return SIZE.includes(value);
			}
		}
	},
	computed: {
		innerOn: {
			// getter
			get() {
				return this.on;
			},
			// setter
			set(newValue: boolean) {
				/**
				 * Whenever value is updated.<br/>
				 * use 'on.sync' in parent component to achieve 2 way binding.
				 *
				 * @event update:value
				 */
				this.$emit("update:on", newValue);
			}
		}
	},
	methods: {
		handleClick(e: MouseEvent) {
			if (!this.disabled) {
				const currentValue = this.innerOn;
				this.innerOn = !currentValue;
				/**
				 * click event
				 *
				 * @event click
				 */

				this.$emit("click", e);
				/**
				 * input event
				 * @event input
				 */
				this.$emit("input", !currentValue);
			}
		}
	}
});
</script>
<style lang="scss">
@use "sass:math";

@mixin flow-switch($off-color, $on-color, $primary, $type-colors) {
	div.flow-switch-container {
		background-color: $off-color;

		@each $type, $color in $type-colors {
			&[type="#{$type}"] {
				&[on] {
					background-color: $color;
				}
			}
		}

		> div.flow-switch-ball {
			background-color: #fff;
		}

		&[disabled] {
			&:before {
				background-color: #000;
			}
		}

		&:not([disabled]) {
			&:focus {
				&:before {
					border: 2px solid $primary;
				}
			}
		}
	}
}

$sizes: (
	"large": 32px,
	"medium": 24px,
	"small": 16px
);

$padding: (
	"large": 4px,
	"medium": 3px,
	"small": 2px
);

div.flow-switch-container {
	display: flex;
	align-items: center;

	cursor: pointer;
	position: relative;
	outline: none;

	transition: all 0.3s linear;

	&[on] {
		justify-content: flex-end;
	}

	@each $name, $size in $sizes {
		&[size="#{$name}"] {
			width: $size * 2 + (map-get($padding, $name) * 2);
			height: $size + (map-get($padding, $name) * 2);
			border-radius: math.div(($size + (map-get($padding, $name) * 2)), 2);
			padding: map-get($padding, $name);
			> div.flow-switch-ball {
				width: $size;
				height: $size;
				border-radius: math.div($size, 2);
			}

			&:not([disabled]) {
				&:focus {
					&:before {
						border-radius: (math.div(($size + (map-get($padding, $name) * 2)), 2)) + 4px;
					}
				}
			}
		}
	}

	&:not([disabled]) {
		&:focus {
			&:before {
				content: "";
				position: absolute;
				//	border-radius: 14px;
				opacity: 0.4;
				top: -4px;
				left: -4px;
				right: -4px;
				bottom: -4px;
			}
		}
	}
	&[disabled] {
		&:before {
			content: "";
			position: absolute;
			border-radius: 10px;
			cursor: not-allowed;
			opacity: 0.4;
			top: 0px;
			left: 0px;
			right: 0px;
			bottom: 0px;
		}
	}
}

@include flow-switch(
	var(--dashboard-element),
	var(--dashboard-success),
	var(--dashboard-primary),
	(
		"primary": var(--dashboard-primary),
		"secondary": var(--dashboard-secondary),
		"success": var(--dashboard-success),
		"error": var(--dashboard-danger),
		"warning": var(--dashboard-warning)
	)
);
.sideout-container {
	@include flow-switch(
		var(--slideout-element),
		var(--slideout-success),
		var(--slideout-primary),
		(
			"primary": var(--slideout-primary),
			"secondary": var(--slideout-secondary),
			"success": var(--slideout-success),
			"error": var(--slideout-danger),
			"warning": var(--slideout-warning)
		)
	);
}
</style>
